export type ReleaseNoteCategory = 'New' | 'Improved' | 'Fixed';

export type ReleaseNoteItem = {
  category: ReleaseNoteCategory;
  title: string;
  description: string;
  audience?: string;
};

export type ReleaseNote = {
  version: string;
  date: string;
  headline: string;
  summary: string;
  highlights: string[];
  items: ReleaseNoteItem[];
  demoUrl?: string;
  demoAspectRatio?: number;
};

export const releaseNotes: ReleaseNote[] = [
  {
    version: '4.3.0',
    date: 'Jul 2026',
    headline: 'Waiting room arrivals, nurse assignment, physician filters, and workflow persistence',
    summary:
      'This release improves waiting room and PreOp visibility, adds configurable nurse assignment and physician filtering on internal screens, and strengthens analytics and patient journey workflows.',
    highlights: [
      'Arrived patients on WR / PreOp screens',
      'Nurse assignment and physician filters',
      'PreOp notes and caretaker info persistence',
    ],
    items: [
      {
        category: 'New',
        title: 'Arrived Patients on WR / PreOp Internal Screen',
        audience: 'Pre-Op Nurses, Front Desk Staff',
        description:
          'Arrived patients can now appear on the Waiting Room tile of the WR / PreOp internal screen, visually differentiated from registered patients. This feature is configurable and requires a WR tablet.',
      },
      {
        category: 'New',
        title: 'Nurse Assignment in PreOp and PACU iPad',
        audience: 'Pre-Op Nurses, PACU Nurses',
        description:
          'Nurses can now be assigned to patients from the PreOp and PACU iPad workflows. This feature is configurable via PreOp and PACU iPad settings.',
      },
      {
        category: 'New',
        title: 'Physician Filter for OR/PR Tile Visibility',
        audience: 'Physicians, OR Staff',
        description:
          'Physicians in OR / PR can now filter the OR TV to show only their own patients. This feature is configurable via internal screen settings.',
      },
      {
        category: 'New',
        title: 'Custom Date Range for Analytics',
        audience: 'Administrators',
        description:
          'Analytics now supports custom date ranges, giving greater flexibility when reviewing analytics data.',
      },
      {
        category: 'Improved',
        title: 'PreOp Notes Persistence',
        audience: 'OR Managers, Schedulers, Nurses',
        description:
          'PreOp notes entered from the Schedule screen now remain persistent as the patient moves into OR / PR.',
      },
      {
        category: 'Improved',
        title: 'Front Desk / Caretaker Info',
        audience: 'Front Desk Staff',
        description:
          'Caretaker phone numbers entered on a WR tablet are now automatically pushed to the Front Desk Dashboard.',
      },
    ],
  },
  {
    version: '4.2.0',
    date: 'Jul 2026',
    headline: 'Release notes, staff attribution, exports, reviews, and workflow updates',
    summary:
      'This release adds a dedicated Release Notes section, expands staff attribution and data export capabilities, and improves patient, nursing, and scheduling workflows.',
    highlights: ['Release Notes module', 'Staff attribution and exports', 'Nursing and scheduling workflows'],
    demoUrl: 'https://app.storylane.io/demo/pvacbqxdr8r2?embed=inline',
    demoAspectRatio: 50.95,
    items: [
      {
        category: 'New',
        title: 'Release Notes Module',
        audience: 'All Users',
        description:
          'A dedicated Release Notes section is now available within the About tab, making it easier to view the latest platform updates and enhancements.',
      },
      {
        category: 'New',
        title: 'Staff Attribution',
        audience: 'Administrators',
        description:
          'Staff costing attribution is now added upon procedure completion, enabling same-day case costing analysis and more accurate operational reporting.',
      },
      {
        category: 'New',
        title: 'Data Export Module',
        audience: 'Administrators',
        description:
          'Predefined analytics and Staff Attribution data can now be exported into CSV and Excel formats for further reporting and analysis.',
      },
      {
        category: 'New',
        title: 'Physician Review SMS',
        audience: 'Physicians',
        description:
          'Patients who submit a 4- or 5-star Ospitek review can now be redirected to their physician’s Google Review page, helping increase provider-specific online reviews.',
      },
      {
        category: 'New',
        title: 'Waiting Room Notes',
        audience: 'Front Desk Staff, Pre-Op Nurses',
        description:
          'Front desk notes can now be displayed on the Internal Screen for patients in the Waiting Room, allowing clinical staff to review important patient information before starting the PreOP process.',
      },
      {
        category: 'New',
        title: 'New KPI: Wheels Out to Next Patient Nerve Block',
        audience: 'Administrators, Nurses',
        description:
          'A new operational KPI measures the time between one patient leaving the OR and the next patient’s nerve block, providing greater insight into perioperative workflow efficiency.',
      },
      {
        category: 'New',
        title: 'PACU iPad View',
        audience: 'Pre-Op Nurses, PACU Nurses',
        description:
          'The PACU iPad workflow is now available to nurse user accounts, allowing Pre-Op and PACU nurses to perform all required actions directly from their computer without interacting with the iPad.',
      },
      {
        category: 'New',
        title: 'Room Change Override',
        audience: 'OR Managers, Schedulers',
        description:
          'Room changes made within Ospitek can now be permanently retained, preventing subsequent EMR updates from overwriting them.',
      },
      {
        category: 'New',
        title: 'Registration Package Default View',
        audience: 'Front Desk Staff',
        description:
          'Today’s Procedures can now be set as the default Registration Package view, replacing the previous Next 7 Days default for quicker access to the current day’s schedule.',
      },
    ],
  },
];
